/**
 * MediaSection.tsx - Media Upload and Management Section
 *
 * This component provides media management capabilities in the fundraiser
 * builder details step including upload, organization, and configuration.
 *
 * Key Features:
 * - Media upload interface with drag-and-drop support
 * - Image and video file support with validation
 * - Media rotation and auto-play settings
 * - Integration with MediaManager component
 * - Preview and editing capabilities
 * - File format and size validation
 *
 * Media Configuration:
 * - Upload multiple images and videos
 * - Set media titles and descriptions
 * - Configure display order and rotation
 * - Enable/disable auto-rotation feature
 * - Set rotation timing and transitions
 *
 * File Support:
 * - Images: JPG, PNG, GIF, WebP formats
 * - Videos: MP4, WebM, embedded YouTube/Vimeo
 * - File size limits and compression
 * - Format validation and error handling
 *
 * Management Features:
 * - Delegated to MediaManager component
 * - Drag-and-drop file upload
 * - Media editing and cropping tools
 * - Bulk operations and organization
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: MediaManager component, file upload utilities, MobX store
 */

import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { fundraiserStore } from "../../../stores/FundraiserStore";
import { MediaManager } from "../MediaManager";

const Section = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #e2e8f0;
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 14px;
  font-weight: 500;
  margin-top: 3px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface MediaSectionProps {
  onKeyDown?: (e: React.KeyboardEvent, isLastField: boolean) => void;
}

export const MediaSection: React.FC<MediaSectionProps> = observer(
  ({ onKeyDown }) => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const hasMediaError = fundraiserStore.builderData.hasError("mediaItems");

    // Auto-open section and scroll to it when there's a media error
    useEffect(() => {
      if (hasMediaError && fundraiserStore.shouldScrollToMediaSection) {
        // Then scroll to it with a delay to allow the section to expand
        setTimeout(() => {
          if (sectionRef.current) {
            const elementTop = sectionRef.current.offsetTop;
            const headerOffset = 100;
            const targetPosition = elementTop - headerOffset;
            window.scrollTo({
              top: targetPosition,
              behavior: "smooth",
            });
          }
          fundraiserStore.setShouldScrollToMediaSection(false);
        }, 150); // Slightly longer delay to ensure section expansion
      } else {
      }
    }, [hasMediaError, fundraiserStore.shouldScrollToMediaSection]);

    return (
      <Section ref={sectionRef} id="media-section">
        <MediaManager onKeyDown={onKeyDown} hasError={hasMediaError} />
        {hasMediaError && (
          <ErrorMessage>
            {fundraiserStore.builderData.getErrorMessage("mediaItems")}
          </ErrorMessage>
        )}
      </Section>
    );
  }
);
